import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { Calculator, FlaskConical, Syringe, Beaker, AlertTriangle, ArrowRight } from 'lucide-react';

/**
 * Reconstitution Calculator, link-magnet SEO page.
 * Covers Tirzepatide, Retatrutide, Semaglutide research-grade peptides.
 * Pure-client math; no backend round-trip.
 *
 * Math:
 *   concentration_mg_per_mL = peptide_mass_mg / bac_water_mL
 *   volume_per_dose_mL      = (target_dose_mcg / 1000) / concentration_mg_per_mL
 *   units_on_u100_syringe   = volume_per_dose_mL * 100   (because U-100 = 1 mL = 100 IU)
 *   total_doses             = bac_water_mL / volume_per_dose_mL
 */

const PEPTIDES = [
  {
    key: 'tirzepatide',
    label: 'Tirzepatide',
    defaultMass: 10,
    defaultDose: 2500,
    // Weekly-ramp research protocol doses (in mcg). Labels shown in mg for
    // one-click convenience -- these match the doses researchers cycle
    // through most often.
    presets: [
      { mcg: 2500,  label: '2.5 mg' },
      { mcg: 5000,  label: '5 mg'   },
      { mcg: 7500,  label: '7.5 mg' },
      { mcg: 10000, label: '10 mg'  },
      { mcg: 12500, label: '12.5 mg' },
      { mcg: 15000, label: '15 mg'  },
    ],
  },
  {
    key: 'retatrutide',
    label: 'Retatrutide',
    defaultMass: 10,
    defaultDose: 2000,
    presets: [
      { mcg: 2000,  label: '2 mg'  },
      { mcg: 4000,  label: '4 mg'  },
      { mcg: 6000,  label: '6 mg'  },
      { mcg: 8000,  label: '8 mg'  },
      { mcg: 12000, label: '12 mg' },
    ],
  },
  {
    key: 'semaglutide',
    label: 'Semaglutide',
    defaultMass: 5,
    defaultDose: 250,
    presets: [
      { mcg: 250,  label: '0.25 mg' },
      { mcg: 500,  label: '0.5 mg'  },
      { mcg: 1000, label: '1 mg'    },
      { mcg: 1700, label: '1.7 mg'  },
      { mcg: 2400, label: '2.4 mg'  },
    ],
  },
];

// Compact label dictionary: 9 supported UI languages.
const STR = {
  en: {
    pageTitle: 'Reconstitution Calculator, Tirzepatide · Retatrutide · Semaglutide',
    metaDesc: 'Free reconstitution calculator for research peptides. Computes mg/mL concentration, mL per dose, and U-100 syringe units for Tirzepatide, Retatrutide, and Semaglutide. For laboratory research only.',
    h1: 'Peptide Reconstitution Calculator',
    sub: 'Compute concentration, dose volume, and U-100 syringe units in seconds. Tirzepatide · Retatrutide · Semaglutide.',
    peptide: 'Peptide',
    peptideMass: 'Peptide mass (mg)',
    bacWater: 'BAC water (mL)',
    targetDose: 'Target dose (mcg)',
    quickPresets: 'Quick presets',
    results: 'Results',
    concentration: 'Concentration',
    volPerDose: 'Volume per dose',
    unitsU100: 'U-100 syringe units',
    totalDoses: 'Total doses per vial',
    howTo: 'How to reconstitute',
    step1: 'Withdraw the calculated volume of bacteriostatic water with a sterile syringe.',
    step2: 'Inject the BAC water slowly down the side wall of the vial, never aim at the powder.',
    step3: 'Swirl gently (do NOT shake) until the lyophilised peptide is fully dissolved and clear.',
    step4: 'Store reconstituted vial refrigerated at 2-8 °C; use within 28 days.',
    tipsTitle: 'Practical tips',
    tip1: 'Most U-100 insulin syringes have 100 units = 1 mL. The "units" output shows what mark to draw to.',
    tip2: 'Round to the nearest half-unit. For doses below 5 units consider using a smaller-volume reconstitution.',
    tip3: 'Use bacteriostatic water (0.9 % benzyl alcohol) for multi-dose vials. Sterile water for single use only.',
    disclaimer: 'STRICTLY FOR LABORATORY RESEARCH USE ONLY. NOT FOR HUMAN OR ANIMAL CONSUMPTION. Not approved by FDA, EMA, or any other regulatory body for therapeutic use. By using this calculator you confirm you are a qualified researcher.',
    relatedTitle: 'Research-grade peptides referenced on this page',
    invalidInput: 'Enter values greater than zero to compute results.',
  },
  es: {
    pageTitle: 'Calculadora de Reconstitución, Tirzepatida · Retatrutida · Semaglutida',
    metaDesc: 'Calculadora gratuita de reconstitución para péptidos de investigación. Calcula concentración mg/mL, mL por dosis y unidades U-100 para Tirzepatida, Retatrutida y Semaglutida. Sólo uso en laboratorio.',
    h1: 'Calculadora de Reconstitución de Péptidos',
    sub: 'Calcula concentración, volumen por dosis y unidades en jeringa U-100 en segundos. Tirzepatida · Retatrutida · Semaglutida.',
    peptide: 'Péptido',
    peptideMass: 'Masa del péptido (mg)',
    bacWater: 'Agua bacteriostática (mL)',
    targetDose: 'Dosis objetivo (mcg)',
    quickPresets: 'Ajustes rápidos',
    results: 'Resultados',
    concentration: 'Concentración',
    volPerDose: 'Volumen por dosis',
    unitsU100: 'Unidades en jeringa U-100',
    totalDoses: 'Dosis totales por vial',
    howTo: 'Cómo reconstituir',
    step1: 'Extrae el volumen calculado de agua bacteriostática con una jeringa estéril.',
    step2: 'Inyecta el agua lentamente por la pared del vial, nunca directamente sobre el polvo.',
    step3: 'Gira suavemente (NO agitar) hasta que el péptido liofilizado esté totalmente disuelto.',
    step4: 'Conserva el vial reconstituido refrigerado a 2-8 °C; utiliza en 28 días.',
    tipsTitle: 'Consejos prácticos',
    tip1: 'La mayoría de jeringas de insulina U-100 son 100 unidades = 1 mL. El valor "unidades" indica la marca a la que llenar.',
    tip2: 'Redondea al medio-unidad más cercano. Para dosis bajo 5 unidades considera reconstituir con menos agua.',
    tip3: 'Usa agua bacteriostática (0,9 % alcohol bencílico) para viales multidosis. El agua estéril es sólo para un uso.',
    disclaimer: 'EXCLUSIVAMENTE PARA INVESTIGACIÓN DE LABORATORIO. NO PARA CONSUMO HUMANO O ANIMAL. No aprobado por FDA, EMA u otra autoridad regulatoria para uso terapéutico. Al usar esta calculadora confirmas que eres un investigador cualificado.',
    relatedTitle: 'Péptidos de grado investigación referenciados en esta página',
    invalidInput: 'Introduce valores mayores que cero para calcular.',
  },
  ru: {
    pageTitle: 'Калькулятор разведения, Тирзепатид · Ретатрутид · Семаглутид',
    metaDesc: 'Бесплатный калькулятор разведения исследовательских пептидов. Расчёт концентрации мг/мл, объёма дозы и делений шприца U-100 для Тирзепатида, Ретатрутида и Семаглутида. Только для лабораторных исследований.',
    h1: 'Калькулятор разведения пептидов',
    sub: 'Рассчитайте концентрацию, объём дозы и деления шприца U-100 за секунды. Тирзепатид · Ретатрутид · Семаглутид.',
    peptide: 'Пептид',
    peptideMass: 'Масса пептида (мг)',
    bacWater: 'Бактериостатическая вода (мл)',
    targetDose: 'Целевая доза (мкг)',
    quickPresets: 'Быстрые пресеты',
    results: 'Результаты',
    concentration: 'Концентрация',
    volPerDose: 'Объём на дозу',
    unitsU100: 'Деления шприца U-100',
    totalDoses: 'Доз во флаконе',
    howTo: 'Как развести',
    step1: 'Наберите рассчитанный объём бактериостатической воды стерильным шприцем.',
    step2: 'Вводите воду медленно по стенке флакона, никогда не направляйте струю на порошок.',
    step3: 'Аккуратно покрутите (НЕ встряхивать) до полного растворения лиофилизата.',
    step4: 'Храните разведённый флакон в холодильнике при 2-8 °C; используйте в течение 28 дней.',
    tipsTitle: 'Практические советы',
    tip1: 'В большинстве инсулиновых шприцев U-100 100 единиц = 1 мл. Значение «деления», это метка, до которой набирать.',
    tip2: 'Округляйте до ближайшего полу-деления. Для доз ниже 5 единиц используйте меньший объём воды.',
    tip3: 'Для многодозовых флаконов используйте бактериостатическую воду (0,9 % бензиловый спирт). Стерильная вода, только для однократного применения.',
    disclaimer: 'ИСКЛЮЧИТЕЛЬНО ДЛЯ ЛАБОРАТОРНЫХ ИССЛЕДОВАНИЙ. НЕ ДЛЯ УПОТРЕБЛЕНИЯ ЧЕЛОВЕКОМ ИЛИ ЖИВОТНЫМИ. Не одобрено FDA, EMA или иными регуляторными органами для терапевтического применения. Используя калькулятор, вы подтверждаете, что являетесь квалифицированным исследователем.',
    relatedTitle: 'Исследовательские пептиды, упомянутые на этой странице',
    invalidInput: 'Введите значения больше нуля для расчёта.',
  },
  de: {
    pageTitle: 'Rekonstitutions-Rechner, Tirzepatid · Retatrutid · Semaglutid',
    metaDesc: 'Kostenloser Rekonstitutions-Rechner für Forschungs-Peptide. Berechnet mg/mL-Konzentration, mL pro Dosis und U-100-Spritzeneinheiten für Tirzepatid, Retatrutid und Semaglutid. Ausschließlich für die Laborforschung.',
    h1: 'Peptid-Rekonstitutions-Rechner',
    sub: 'Konzentration, Dosisvolumen und U-100-Spritzeneinheiten in Sekunden berechnen. Tirzepatid · Retatrutid · Semaglutid.',
    peptide: 'Peptid',
    peptideMass: 'Peptid-Masse (mg)',
    bacWater: 'BAC-Wasser (mL)',
    targetDose: 'Zieldosis (mcg)',
    quickPresets: 'Schnellauswahl',
    results: 'Ergebnisse',
    concentration: 'Konzentration',
    volPerDose: 'Volumen pro Dosis',
    unitsU100: 'U-100 Spritzeneinheiten',
    totalDoses: 'Dosen pro Vial',
    howTo: 'So wird rekonstituiert',
    step1: 'Berechnetes Volumen bakteriostatisches Wasser mit steriler Spritze aufziehen.',
    step2: 'Wasser langsam an der Vial-Wand entlang einspritzen, niemals direkt auf das Pulver.',
    step3: 'Sanft schwenken (NICHT schütteln), bis das Lyophilisat vollständig gelöst ist.',
    step4: 'Rekonstituiertes Vial bei 2-8 °C kühl lagern; innerhalb von 28 Tagen verwenden.',
    tipsTitle: 'Praktische Tipps',
    tip1: 'Bei den meisten U-100-Insulinspritzen entspricht 100 Einheiten = 1 mL. Der "Einheiten"-Wert zeigt, bis wohin aufzuziehen ist.',
    tip2: 'Auf nächste Halbeinheit runden. Für Dosen unter 5 Einheiten weniger Wasser verwenden.',
    tip3: 'Bakteriostatisches Wasser (0,9 % Benzylalkohol) für Mehrdosis-Vials. Steriles Wasser nur für Einmal-Gebrauch.',
    disclaimer: 'AUSSCHLIESSLICH ZU LABORFORSCHUNGSZWECKEN. NICHT FÜR DEN MENSCHLICHEN ODER TIERISCHEN VERZEHR. Nicht von FDA, EMA oder anderen Behörden für therapeutische Anwendung zugelassen. Mit der Nutzung bestätigen Sie, qualifizierter Forscher zu sein.',
    relatedTitle: 'Auf dieser Seite referenzierte Forschungs-Peptide',
    invalidInput: 'Bitte Werte größer als Null eingeben.',
  },
  fr: {
    pageTitle: 'Calculatrice de Reconstitution, Tirzépatide · Rétatrutide · Sémaglutide',
    metaDesc: 'Calculatrice gratuite de reconstitution de peptides de recherche. Calcule la concentration mg/mL, le volume par dose et les unités sur seringue U-100. Réservé à la recherche en laboratoire.',
    h1: 'Calculatrice de Reconstitution de Peptides',
    sub: 'Concentration, volume par dose et unités U-100 en quelques secondes. Tirzépatide · Rétatrutide · Sémaglutide.',
    peptide: 'Peptide',
    peptideMass: 'Masse du peptide (mg)',
    bacWater: 'Eau bactériostatique (mL)',
    targetDose: 'Dose cible (mcg)',
    quickPresets: 'Préréglages rapides',
    results: 'Résultats',
    concentration: 'Concentration',
    volPerDose: 'Volume par dose',
    unitsU100: 'Unités sur seringue U-100',
    totalDoses: 'Doses totales par flacon',
    howTo: 'Comment reconstituer',
    step1: "Aspirez le volume calculé d'eau bactériostatique avec une seringue stérile.",
    step2: "Injectez l'eau lentement le long de la paroi du flacon, jamais directement sur la poudre.",
    step3: 'Agitez doucement par rotation (NE PAS secouer) jusqu\'à dissolution complète.',
    step4: 'Conservez le flacon reconstitué à 2-8 °C ; utilisez dans les 28 jours.',
    tipsTitle: 'Conseils pratiques',
    tip1: "La plupart des seringues à insuline U-100 ont 100 unités = 1 mL. La valeur « unités » indique le repère.",
    tip2: "Arrondissez à la demi-unité la plus proche. Pour des doses < 5 unités, utilisez moins d'eau.",
    tip3: "Utilisez de l'eau bactériostatique (0,9 % alcool benzylique) pour flacons multidoses.",
    disclaimer: 'STRICTEMENT POUR LA RECHERCHE EN LABORATOIRE. PAS POUR LA CONSOMMATION HUMAINE OU ANIMALE. Non approuvé par FDA, EMA ou tout autre organisme pour usage thérapeutique. En utilisant cette calculatrice vous confirmez être un chercheur qualifié.',
    relatedTitle: 'Peptides de recherche mentionnés sur cette page',
    invalidInput: 'Entrez des valeurs supérieures à zéro pour calculer.',
  },
  it: {
    pageTitle: 'Calcolatore di Ricostituzione, Tirzepatide · Retatrutide · Semaglutide',
    metaDesc: 'Calcolatore gratuito di ricostituzione di peptidi da ricerca. Calcola concentrazione mg/mL, mL per dose e unità su siringa U-100. Solo per ricerca di laboratorio.',
    h1: 'Calcolatore di Ricostituzione di Peptidi',
    sub: 'Concentrazione, volume per dose e unità U-100 in pochi secondi. Tirzepatide · Retatrutide · Semaglutide.',
    peptide: 'Peptide',
    peptideMass: 'Massa del peptide (mg)',
    bacWater: 'Acqua batteriostatica (mL)',
    targetDose: 'Dose obiettivo (mcg)',
    quickPresets: 'Preset rapidi',
    results: 'Risultati',
    concentration: 'Concentrazione',
    volPerDose: 'Volume per dose',
    unitsU100: 'Unità su siringa U-100',
    totalDoses: 'Dosi totali per flaconcino',
    howTo: 'Come ricostituire',
    step1: 'Aspira il volume calcolato di acqua batteriostatica con una siringa sterile.',
    step2: 'Inietta l\'acqua lentamente lungo la parete del flaconcino, mai direttamente sulla polvere.',
    step3: 'Ruota delicatamente (NON agitare) fino a completa dissoluzione del peptide liofilizzato.',
    step4: 'Conserva il flaconcino ricostituito a 2-8 °C; usa entro 28 giorni.',
    tipsTitle: 'Consigli pratici',
    tip1: 'Le siringhe da insulina U-100 hanno 100 unità = 1 mL. Il valore "unità" indica il segno a cui aspirare.',
    tip2: 'Arrotonda alla mezza unità più vicina. Per dosi < 5 unità, usa meno acqua.',
    tip3: 'Usa acqua batteriostatica (0,9 % alcol benzilico) per flaconcini multidose.',
    disclaimer: 'ESCLUSIVAMENTE PER RICERCA DI LABORATORIO. NON PER CONSUMO UMANO O ANIMALE. Non approvato da FDA, EMA o altri enti regolatori per uso terapeutico. Usando questo calcolatore confermi di essere un ricercatore qualificato.',
    relatedTitle: 'Peptidi da ricerca citati in questa pagina',
    invalidInput: 'Inserisci valori maggiori di zero per calcolare.',
  },
  pl: {
    pageTitle: 'Kalkulator Rozcieńczania, Tyrzepatyd · Retatrutyd · Semaglutyd',
    metaDesc: 'Bezpłatny kalkulator rozcieńczania peptydów badawczych. Oblicza stężenie mg/mL, mL na dawkę i jednostki strzykawki U-100. Wyłącznie do badań laboratoryjnych.',
    h1: 'Kalkulator Rozcieńczania Peptydów',
    sub: 'Stężenie, objętość dawki i jednostki U-100 w kilka sekund. Tyrzepatyd · Retatrutyd · Semaglutyd.',
    peptide: 'Peptyd',
    peptideMass: 'Masa peptydu (mg)',
    bacWater: 'Woda bakteriostatyczna (mL)',
    targetDose: 'Dawka docelowa (mcg)',
    quickPresets: 'Szybkie ustawienia',
    results: 'Wyniki',
    concentration: 'Stężenie',
    volPerDose: 'Objętość na dawkę',
    unitsU100: 'Jednostki strzykawki U-100',
    totalDoses: 'Dawek na fiolkę',
    howTo: 'Jak rozcieńczyć',
    step1: 'Pobierz obliczoną objętość wody bakteriostatycznej sterylną strzykawką.',
    step2: 'Wprowadź wodę powoli po ściance fiolki, nigdy bezpośrednio na proszek.',
    step3: 'Delikatnie obróć (NIE wstrząsać) do całkowitego rozpuszczenia liofilizatu.',
    step4: 'Przechowuj rozcieńczoną fiolkę w 2-8 °C; zużyj w ciągu 28 dni.',
    tipsTitle: 'Praktyczne wskazówki',
    tip1: 'W większości strzykawek insulinowych U-100 100 jednostek = 1 mL. Wartość „jednostki" to znacznik na strzykawce.',
    tip2: 'Zaokrąglij do najbliższej pół-jednostki. Dla dawek poniżej 5 jednostek użyj mniej wody.',
    tip3: 'Do fiolek wielodawkowych używaj wody bakteriostatycznej (0,9 % alkohol benzylowy).',
    disclaimer: 'WYŁĄCZNIE DO BADAŃ LABORATORYJNYCH. NIE DO SPOŻYCIA PRZEZ LUDZI ANI ZWIERZĘTA. Nie zatwierdzone przez FDA, EMA ani inne organy regulacyjne do zastosowania terapeutycznego. Korzystając z kalkulatora potwierdzasz, że jesteś wykwalifikowanym badaczem.',
    relatedTitle: 'Peptydy badawcze wymienione na tej stronie',
    invalidInput: 'Wprowadź wartości większe od zera, aby obliczyć.',
  },
  nl: {
    pageTitle: 'Reconstitutie-calculator, Tirzepatide · Retatrutide · Semaglutide',
    metaDesc: 'Gratis reconstitutie-calculator voor onderzoekspeptiden. Berekent mg/mL-concentratie, mL per dosis en U-100-spuiteenheden. Uitsluitend voor laboratoriumonderzoek.',
    h1: 'Peptide Reconstitutie-calculator',
    sub: 'Concentratie, dosisvolume en U-100-spuiteenheden in seconden. Tirzepatide · Retatrutide · Semaglutide.',
    peptide: 'Peptide',
    peptideMass: 'Peptidemassa (mg)',
    bacWater: 'BAC-water (mL)',
    targetDose: 'Doeldosis (mcg)',
    quickPresets: 'Snelle voorinstellingen',
    results: 'Resultaten',
    concentration: 'Concentratie',
    volPerDose: 'Volume per dosis',
    unitsU100: 'U-100-spuiteenheden',
    totalDoses: 'Doses per flacon',
    howTo: 'Hoe te reconstitueren',
    step1: 'Trek het berekende volume bacteriostatisch water op met een steriele spuit.',
    step2: 'Spuit het water langzaam langs de wand van de flacon, nooit op het poeder.',
    step3: 'Zwenk voorzichtig (NIET schudden) tot het lyofilisaat volledig is opgelost.',
    step4: 'Bewaar de gereconstitueerde flacon bij 2-8 °C; gebruik binnen 28 dagen.',
    tipsTitle: 'Praktische tips',
    tip1: 'De meeste U-100-insulinespuiten zijn 100 eenheden = 1 mL. De "eenheden"-waarde geeft de markering aan.',
    tip2: 'Rond af op de dichtstbijzijnde halve eenheid. Bij doses < 5 eenheden minder water gebruiken.',
    tip3: 'Gebruik bacteriostatisch water (0,9 % benzylalcohol) voor multi-dose flacons.',
    disclaimer: 'UITSLUITEND VOOR LABORATORIUMONDERZOEK. NIET VOOR MENSELIJKE OF DIERLIJKE CONSUMPTIE. Niet goedgekeurd door FDA, EMA of andere instanties voor therapeutisch gebruik. Door deze calculator te gebruiken bevestigt u een gekwalificeerd onderzoeker te zijn.',
    relatedTitle: 'Onderzoekspeptiden genoemd op deze pagina',
    invalidInput: 'Voer waarden groter dan nul in om te berekenen.',
  },
  pt: {
    pageTitle: 'Calculadora de Reconstituição, Tirzepatida · Retatrutida · Semaglutida',
    metaDesc: 'Calculadora gratuita de reconstituição de peptídeos de pesquisa. Calcula concentração mg/mL, mL por dose e unidades em seringa U-100. Apenas para pesquisa em laboratório.',
    h1: 'Calculadora de Reconstituição de Peptídeos',
    sub: 'Concentração, volume por dose e unidades U-100 em segundos. Tirzepatida · Retatrutida · Semaglutida.',
    peptide: 'Peptídeo',
    peptideMass: 'Massa do peptídeo (mg)',
    bacWater: 'Água bacteriostática (mL)',
    targetDose: 'Dose alvo (mcg)',
    quickPresets: 'Predefinições rápidas',
    results: 'Resultados',
    concentration: 'Concentração',
    volPerDose: 'Volume por dose',
    unitsU100: 'Unidades em seringa U-100',
    totalDoses: 'Doses por frasco',
    howTo: 'Como reconstituir',
    step1: 'Aspire o volume calculado de água bacteriostática com seringa estéril.',
    step2: 'Injete a água lentamente pela parede do frasco, nunca direto no pó.',
    step3: 'Gire suavemente (NÃO agite) até o liofilizado dissolver completamente.',
    step4: 'Conserve o frasco reconstituído a 2-8 °C; use em 28 dias.',
    tipsTitle: 'Dicas práticas',
    tip1: 'A maioria das seringas de insulina U-100 são 100 unidades = 1 mL. O valor "unidades" é a marca a aspirar.',
    tip2: 'Arredonde para a meia unidade mais próxima. Para doses < 5 unidades, use menos água.',
    tip3: 'Use água bacteriostática (0,9 % álcool benzílico) para frascos multi-dose.',
    disclaimer: 'EXCLUSIVAMENTE PARA PESQUISA LABORATORIAL. NÃO PARA CONSUMO HUMANO OU ANIMAL. Não aprovado pela FDA, EMA ou outros órgãos para uso terapêutico. Ao usar esta calculadora você confirma ser um pesquisador qualificado.',
    relatedTitle: 'Peptídeos de pesquisa referenciados nesta página',
    invalidInput: 'Insira valores maiores que zero para calcular.',
  },
};

const fmt = (v, digits = 3) => {
  if (!Number.isFinite(v) || v <= 0) return ', ';
  // Drop trailing zeros for readability
  return parseFloat(v.toFixed(digits)).toString();
};

const ReconstitutionCalculatorPage = () => {
  const { currentLanguage, t: tg } = useLanguage();
  const s = STR[currentLanguage] || STR.en;

  const [peptideKey, setPeptideKey] = useState('tirzepatide');
  const peptide = PEPTIDES.find((p) => p.key === peptideKey) || PEPTIDES[0];
  const [mass, setMass] = useState(peptide.defaultMass);
  const [water, setWater] = useState(2);
  const [dose, setDose] = useState(peptide.defaultDose);

  const onPeptideChange = (key) => {
    const p = PEPTIDES.find((x) => x.key === key) || PEPTIDES[0];
    setPeptideKey(key);
    setMass(p.defaultMass);
    setDose(p.defaultDose);
  };

  const calc = useMemo(() => {
    const m = parseFloat(mass);
    const w = parseFloat(water);
    const d = parseFloat(dose);
    if (!(m > 0 && w > 0 && d > 0)) {
      return { ok: false };
    }
    const concentration = m / w;            // mg/mL
    const volPerDose    = (d / 1000) / concentration; // mL
    const u100Units     = volPerDose * 100; // units
    const totalDoses    = w / volPerDose;
    return { ok: true, concentration, volPerDose, u100Units, totalDoses };
  }, [mass, water, dose]);

  // JSON-LD: HowTo + WebApplication for SEO rich results.
  const howToLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: s.h1,
    description: s.metaDesc,
    inLanguage: currentLanguage,
    tool: [
      { '@type': 'HowToTool', name: 'Bacteriostatic water (BAC), 0.9% benzyl alcohol' },
      { '@type': 'HowToTool', name: 'Sterile reconstitution syringe' },
      { '@type': 'HowToTool', name: 'U-100 insulin syringe' },
      { '@type': 'HowToTool', name: 'Lyophilised research peptide vial (Tirzepatide / Retatrutide / Semaglutide)' },
    ],
    step: [
      { '@type': 'HowToStep', position: 1, name: 'Withdraw BAC water', text: s.step1 },
      { '@type': 'HowToStep', position: 2, name: 'Inject down vial wall', text: s.step2 },
      { '@type': 'HowToStep', position: 3, name: 'Swirl gently',         text: s.step3 },
      { '@type': 'HowToStep', position: 4, name: 'Refrigerate',          text: s.step4 },
    ],
  }), [s, currentLanguage]);

  const appLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'PenLab Peptide Reconstitution Calculator',
    applicationCategory: 'ResearchTool',
    operatingSystem: 'Any (web)',
    inLanguage: currentLanguage,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    isAccessibleForFree: true,
  }), [currentLanguage]);

  // Imperative head-update fallback: react-helmet-async does not flush in this
  // app (verified, same workaround used in SEOHead.js). Manually mutate the
  // document head so search engines and social crawlers see the right metadata.
  useEffect(() => {
    try {
      document.title = s.pageTitle;
      document.documentElement.lang = currentLanguage;

      // Build prefix-aware canonical so /fr/calculators/reconstitution canonicalises
      // to itself, not to the bare path. Mirrors the SEOHead.js helper.
      const SUPPORTED_LANGS = ['en','es','ru','de','fr','it','pl','nl','pt'];
      const path = (typeof window !== 'undefined' && window.location.pathname) || '/calculators/reconstitution';
      const seg = path.split('/')[1];
      const stripped = SUPPORTED_LANGS.includes(seg)
        ? (path.replace(`/${seg}`, '') || '/calculators/reconstitution')
        : '/calculators/reconstitution';
      const canonical = `https://penlabpeptide.com/${currentLanguage}${stripped === '/' ? '' : stripped}`;

      const setMeta = (selector, value, attr = 'content') => {
        let el = document.querySelector(selector);
        if (!el) {
          // Create on the fly if missing, required for og/canonical when index.html lacks them.
          const m = selector.match(/^meta\[(name|property)="([^"]+)"\]$/);
          if (m) {
            el = document.createElement('meta');
            el.setAttribute(m[1], m[2]);
            document.head.appendChild(el);
          } else if (selector === 'link[rel="canonical"]') {
            el = document.createElement('link');
            el.setAttribute('rel', 'canonical');
            document.head.appendChild(el);
          }
        }
        if (el) el.setAttribute(attr, value);
      };
      setMeta('meta[name="description"]', s.metaDesc);
      setMeta('meta[name="title"]', s.pageTitle);
      setMeta('meta[property="og:title"]', s.pageTitle);
      setMeta('meta[property="og:description"]', s.metaDesc);
      setMeta('meta[property="og:type"]', 'website');
      setMeta('meta[property="twitter:title"]', s.pageTitle);
      setMeta('meta[property="twitter:description"]', s.metaDesc);
      setMeta('link[rel="canonical"]', canonical, 'href');

      // Replace any pre-existing hreflang block with a prefix-aware one (10 langs + x-default).
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      SUPPORTED_LANGS.forEach((lc) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lc);
        link.setAttribute('href', `https://penlabpeptide.com/${lc}${stripped === '/' ? '' : stripped}`);
        document.head.appendChild(link);
      });
      const xd = document.createElement('link');
      xd.setAttribute('rel', 'alternate');
      xd.setAttribute('hreflang', 'x-default');
      xd.setAttribute('href', `https://penlabpeptide.com${stripped}`);
      document.head.appendChild(xd);

      // JSON-LD scripts: replace any previously-injected calculator scripts to
      // keep them in sync with the active language.
      const upsertLd = (id, payload) => {
        let s2 = document.getElementById(id);
        if (!s2) {
          s2 = document.createElement('script');
          s2.id = id;
          s2.type = 'application/ld+json';
          document.head.appendChild(s2);
        }
        s2.textContent = JSON.stringify(payload);
      };
      upsertLd('ld-recon-calc-howto', howToLd);
      upsertLd('ld-recon-calc-app', appLd);

      // Cleanup is intentionally NOT done, leaving the JSON-LD on subsequent
      // pages would only ever apply to /calculators/reconstitution and these
      // upserts run on mount. Remove on unmount to avoid leaking schema.
      return () => {
        ['ld-recon-calc-howto', 'ld-recon-calc-app'].forEach((id) => {
          const node = document.getElementById(id);
          if (node && node.parentNode) node.parentNode.removeChild(node);
        });
      };
    } catch (_) {
      // never break render over head mutations
    }
  }, [s, howToLd, appLd, currentLanguage]);

  return (
    <>
      <Helmet>
        <title>{s.pageTitle}</title>
        <meta name="description" content={s.metaDesc} />
        <meta property="og:title" content={s.pageTitle} />
        <meta property="og:description" content={s.metaDesc} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://penlabpeptide.com/calculators/reconstitution" />
        <script type="application/ld+json">{JSON.stringify(howToLd)}</script>
        <script type="application/ld+json">{JSON.stringify(appLd)}</script>
      </Helmet>

      <Header />

      <main className="bg-gradient-to-b from-slate-50 via-white to-slate-50 min-h-screen pb-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-teal-700 via-teal-800 to-slate-900 text-white pt-12 pb-16">
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-4 text-teal-200">
              <Calculator className="w-6 h-6" />
              <span className="uppercase tracking-widest text-xs font-semibold">{tg('calc_research_tool')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4" data-testid="recon-calc-h1">
              {s.h1}
            </h1>
            <p className="text-lg sm:text-xl text-teal-100 max-w-3xl">{s.sub}</p>
          </div>
        </section>

        {/* Calculator card */}
        <section className="max-w-5xl mx-auto px-4 -mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200" data-testid="recon-calc-inputs">
              <h2 className="text-base md:text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-teal-600" />
                {s.peptide} · BAC · {s.targetDose}
              </h2>

              <label className="block mb-4">
                <span className="text-sm font-semibold text-slate-700">{s.peptide}</span>
                <select
                  className="mt-1 w-full rounded-lg border-slate-300 focus:border-teal-500 focus:ring-teal-500 py-2 px-3"
                  value={peptideKey}
                  onChange={(e) => onPeptideChange(e.target.value)}
                  data-testid="recon-calc-peptide-select"
                >
                  {PEPTIDES.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </label>

              <label className="block mb-4">
                <span className="text-sm font-semibold text-slate-700">{s.peptideMass}</span>
                <input
                  type="number" min="0" step="0.5" value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  className="mt-1 w-full rounded-lg border-slate-300 focus:border-teal-500 focus:ring-teal-500 py-2 px-3"
                  data-testid="recon-calc-mass-input"
                />
              </label>

              <label className="block mb-4">
                <span className="text-sm font-semibold text-slate-700">{s.bacWater}</span>
                <input
                  type="number" min="0" step="0.1" value={water}
                  onChange={(e) => setWater(e.target.value)}
                  className="mt-1 w-full rounded-lg border-slate-300 focus:border-teal-500 focus:ring-teal-500 py-2 px-3"
                  data-testid="recon-calc-water-input"
                />
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-700">{s.targetDose}</span>
                <input
                  type="number" min="0" step="50" value={dose}
                  onChange={(e) => setDose(e.target.value)}
                  className="mt-1 w-full rounded-lg border-slate-300 focus:border-teal-500 focus:ring-teal-500 py-2 px-3"
                  data-testid="recon-calc-dose-input"
                />
                {peptide.presets && peptide.presets.length > 0 && (
                  <div className="mt-3" data-testid="recon-calc-presets">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {s.quickPresets}
                    </span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {peptide.presets.map((p) => {
                        const active = Number(dose) === p.mcg;
                        return (
                          <button
                            key={p.mcg}
                            type="button"
                            onClick={() => setDose(p.mcg)}
                            className={
                              'px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ' +
                              (active
                                ? 'bg-teal-600 text-white border-teal-600'
                                : 'bg-white text-slate-700 border-slate-300 hover:border-teal-500 hover:text-teal-700')
                            }
                            data-testid={`recon-calc-preset-${peptide.key}-${p.mcg}`}
                            aria-pressed={active}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </label>
            </div>

            {/* Outputs */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl shadow-xl p-6" data-testid="recon-calc-results">
              <h2 className="text-base md:text-lg font-bold mb-5 flex items-center gap-2">
                <Syringe className="w-5 h-5 text-teal-300" />
                {s.results}
              </h2>

              {!calc.ok && (
                <p className="text-amber-300 text-sm" data-testid="recon-calc-invalid">{s.invalidInput}</p>
              )}

              {calc.ok && (
                <div className="space-y-5">
                  <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
                    <span className="text-sm text-slate-300">{s.concentration}</span>
                    <span className="text-2xl font-extrabold text-teal-300" data-testid="recon-calc-concentration">
                      {fmt(calc.concentration)} mg/mL
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
                    <span className="text-sm text-slate-300">{s.volPerDose}</span>
                    <span className="text-2xl font-extrabold text-teal-300" data-testid="recon-calc-volume">
                      {fmt(calc.volPerDose)} mL
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
                    <span className="text-sm text-slate-300">{s.unitsU100}</span>
                    <span className="text-3xl font-extrabold text-amber-300" data-testid="recon-calc-units">
                      {fmt(calc.u100Units, 1)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-slate-300">{s.totalDoses}</span>
                    <span className="text-2xl font-extrabold text-teal-300" data-testid="recon-calc-total-doses">
                      {fmt(calc.totalDoses, 1)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* How to */}
        <section className="max-w-5xl mx-auto px-4 mt-12">
          <div className="bg-white rounded-2xl shadow-md p-6 border border-slate-200">
            <h2 className="text-base md:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Beaker className="w-5 h-5 text-teal-600" />
              {s.howTo}
            </h2>
            <ol className="space-y-3 list-decimal list-inside text-slate-700 text-sm md:text-base">
              <li>{s.step1}</li>
              <li>{s.step2}</li>
              <li>{s.step3}</li>
              <li>{s.step4}</li>
            </ol>

            <h3 className="text-sm md:text-base font-bold text-slate-900 mt-7 mb-3">{s.tipsTitle}</h3>
            <ul className="space-y-2 list-disc list-inside text-slate-700 text-sm md:text-base">
              <li>{s.tip1}</li>
              <li>{s.tip2}</li>
              <li>{s.tip3}</li>
            </ul>
          </div>
        </section>

        {/* Internal-link card to product pages */}
        <section className="max-w-5xl mx-auto px-4 mt-8">
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-bold text-teal-900 mb-4">{s.relatedTitle}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="recon-calc-related-links">
              {PEPTIDES.map((p) => (
                <Link
                  key={p.key}
                  to={`/products?search=${encodeURIComponent(p.label)}`}
                  className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-teal-200 hover:border-teal-400 hover:shadow-md transition-all"
                  data-testid={`recon-calc-related-${p.key}`}
                >
                  <span className="font-semibold text-teal-900">{p.label}</span>
                  <ArrowRight className="w-4 h-4 text-teal-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* RUO disclaimer */}
        <section className="max-w-5xl mx-auto px-4 mt-8">
          <div className="bg-rose-50 border-l-4 border-rose-500 rounded-r-lg p-5 flex gap-3 items-start">
            <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm text-rose-900 leading-relaxed" data-testid="recon-calc-disclaimer">
              {s.disclaimer}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ReconstitutionCalculatorPage;
